import { Box, Center, Flex, HStack, Spinner, Tooltip } from '@chakra-ui/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useIsRevelioAdmin } from '@revelio/auth';
import {
  Button,
  DetailsWithDescription,
  NO_MAX_DETAIL_LIMIT,
  Option,
  Text,
} from '@revelio/ui';

import {
  CircleCheckEmptyIcon,
  CircleCheckedIcon,
  DeliverableIcons,
  DeliverablesIconLookup,
} from '../../../shared/ui/icons';
import { useClientDataBuilderConfig } from '../api/useClientDataBuilderConfig';
import { DataBuilderFooter } from '../components/DataBuilderFooter';
import { DataBuilderStepLayout } from '../components/ui/DataBuilderStepLayout';
import { OptionInteractiveContent } from '../components/ui/OptionInteractiveContent';
import {
  type DeliveryMethod,
  selectDeliveryConfigured,
  selectDeliveryFormat,
  selectDeliveryMethod,
  selectS3Config,
  selectSnowflakeConfig,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';
import type { DeliveryFormHandle } from './step3-delivery/DeliveryConfigForm.types';
import { DownloadConfigForm } from './step3-delivery/DownloadConfigForm';
import { S3ConfigForm } from './step3-delivery/S3ConfigForm';
import { SnowflakeConfigForm } from './step3-delivery/SnowflakeConfigForm';
import { useVerifyDataBuilder } from './step3-delivery/useVerifyDataBuilder';

const DELIVERY_METHOD_OPTIONS: {
  value: DeliveryMethod;
  label: string;
  detail: string;
  icon: DeliverableIcons;
}[] = [
  {
    value: 'download',
    label: 'Download',
    detail:
      "Receive your data as a zipped package that contains all the files you've selected",
    icon: DeliverableIcons.DOWNLOAD,
  },
  {
    value: 'snowflake',
    label: 'Snowflake Database',
    detail: 'Receive your data in Snowflake.',
    icon: DeliverableIcons.SNOWFLAKE,
  },
  {
    value: 's3',
    label: 'AWS S3 Bucket',
    detail: 'Receive your data in a private S3 Bucket.',
    icon: DeliverableIcons.AWS_S3_BUCKET,
  },
];

type DeliveryMethodOptionProps = {
  value: DeliveryMethod;
  label: string;
  detail: string;
  icon: DeliverableIcons;
  isSelected: boolean;
  disabled?: boolean;
  onClick: () => void;
  formContent: ReactNode;
  verifyError?: ReactNode;
};

const DeliveryMethodOption = ({
  value,
  label,
  detail,
  icon,
  isSelected,
  disabled,
  onClick,
  formContent,
  verifyError,
}: DeliveryMethodOptionProps) => {
  const Icon = isSelected ? CircleCheckedIcon : CircleCheckEmptyIcon;
  const DeliveryIcon = DeliverablesIconLookup[icon];

  return (
    <Option
      data-testid={`data-builder-delivery-method-${value}`}
      style={{ minHeight: 0, alignItems: 'flex-start' }}
      isSelected={isSelected}
      disabled={disabled}
      onClick={onClick}
      startSlotAlignment="start"
      contentAlignment="start"
      startSlot={
        <Flex align="center" gap="3">
          <Icon size={20} />
          {DeliveryIcon !== undefined && (
            <Box bg="#E5EBF1" p="12px" borderRadius="50%">
              <Center>
                <DeliveryIcon boxSize={6} />
              </Center>
            </Box>
          )}
        </Flex>
      }
    >
      <DetailsWithDescription
        title={label}
        detail={detail}
        maxDetailLines={NO_MAX_DETAIL_LIMIT}
      />
      <OptionInteractiveContent isOpen={isSelected}>
        {formContent}
        {verifyError}
      </OptionInteractiveContent>
    </Option>
  );
};

export const DataBuilderDeliveryStep = () => {
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const clientDataBuilderConfig = useClientDataBuilderConfig();
  const deliveryMethod = useDataBuilderSelector(selectDeliveryMethod);
  const deliveryFormat = useDataBuilderSelector(selectDeliveryFormat);
  const deliveryConfigured = useDataBuilderSelector(selectDeliveryConfigured);
  const snowflakeConfig = useDataBuilderSelector(selectSnowflakeConfig);
  const s3Config = useDataBuilderSelector(selectS3Config);
  const { goToNextStep } = useDataBuilderNavigation();
  const {
    setDeliveryMethod,
    setDeliveryFormat,
    setSnowflakeConfig,
    setS3Config,
  } = useDataBuilderActions();
  const activeFormRef = useRef<DeliveryFormHandle | null>(null);
  const wasVerifyErrorRef = useRef(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const [verifyErrorMethod, setVerifyErrorMethod] =
    useState<DeliveryMethod | null>(null);
  const {
    isPending: isVerifying,
    isError: isVerifyError,
    errorMessage: verifyErrorMessage,
    isReady: isVerifyReady,
    verifyAsync,
  } = useVerifyDataBuilder();
  const isDownloadMethod = deliveryMethod === 'download';

  const continueDisabledReason = (() => {
    if (isVerifying || isContinuing) {
      return 'Verifying configuration...';
    }
    if (isDownloadMethod && !deliveryConfigured) {
      return 'Configure your delivery method to continue.';
    }
    if (isDownloadMethod && isVerifyError) {
      return 'Resolve the configuration issue to continue.';
    }
    if (isDownloadMethod && !isVerifyReady) {
      return 'Verifying configuration...';
    }
    return '';
  })();
  const isContinueDisabled = continueDisabledReason !== '';

  useEffect(() => {
    const isNewVerifyError = isVerifyError && !wasVerifyErrorRef.current;
    if (isNewVerifyError) {
      setVerifyErrorMethod(deliveryMethod);
    } else if (isVerifyReady) {
      setVerifyErrorMethod(null);
    }
    wasVerifyErrorRef.current = isVerifyError;
  }, [deliveryMethod, isVerifyError, isVerifyReady]);

  const hasS3DefaultLocation =
    clientDataBuilderConfig?.s3_location !== undefined &&
    clientDataBuilderConfig.s3_location !== null;
  const hasSnowflakeDefaultLocation =
    clientDataBuilderConfig?.snowflake_location !== undefined &&
    clientDataBuilderConfig.snowflake_location !== null;

  const deliveryOptions = DELIVERY_METHOD_OPTIONS.map((option) => {
    if (isRevelioAdmin) {
      return { ...option, disabled: false };
    }
    if (option.value === 'snowflake') {
      return { ...option, disabled: !hasSnowflakeDefaultLocation };
    }
    if (option.value === 's3') {
      return { ...option, disabled: !hasS3DefaultLocation };
    }
    return { ...option, disabled: false };
  });

  const selectMethod = (method: DeliveryMethod) => {
    if (method === deliveryMethod) {
      return;
    }
    setDeliveryMethod(method);
    setVerifyErrorMethod(null);
  };

  const shouldShowSelectedMethodVerifyError =
    isVerifyError && verifyErrorMethod === deliveryMethod;

  const handleContinue = async () => {
    if (isDownloadMethod) {
      if (isVerifyReady) {
        goToNextStep();
      }
      return;
    }

    setIsContinuing(true);
    try {
      const formRef = activeFormRef.current;
      if (formRef !== null && (!deliveryConfigured || formRef.isDirty())) {
        const isValid = await formRef.submit();
        if (!isValid) {
          return;
        }
      }
      await verifyAsync();
      goToNextStep();
    } catch {
      // Verification failed; the inline error UI reflects the mutation state.
    } finally {
      setIsContinuing(false);
    }
  };

  const getOptionFormContent = (method: DeliveryMethod): ReactNode => {
    if (method === 'download') {
      return (
        <DownloadConfigForm
          initialValue={deliveryFormat}
          onSave={(format) => {
            setDeliveryFormat(format);
          }}
        />
      );
    }
    if (method === 'snowflake') {
      return (
        <SnowflakeConfigForm
          ref={deliveryMethod === 'snowflake' ? activeFormRef : null}
          initialValue={snowflakeConfig}
          defaultDatabase={
            clientDataBuilderConfig?.snowflake_location?.snowflake_db ?? null
          }
          defaultSchema={
            clientDataBuilderConfig?.snowflake_location?.snowflake_schema ??
            null
          }
          isRevelioAdmin={isRevelioAdmin}
          onSave={setSnowflakeConfig}
        />
      );
    }
    if (method === 's3') {
      return (
        <S3ConfigForm
          ref={deliveryMethod === 's3' ? activeFormRef : null}
          initialValue={s3Config}
          defaultBucket={
            clientDataBuilderConfig?.s3_location?.s3_bucket ?? null
          }
          isRevelioAdmin={isRevelioAdmin}
          onSave={setS3Config}
        />
      );
    }
    return null;
  };

  return (
    <DataBuilderStepLayout
      title="Delivery"
      footer={
        <DataBuilderFooter
          actions={
            <Tooltip
              label={continueDisabledReason}
              isDisabled={!isContinueDisabled}
              hasArrow
              placement="top"
              shouldWrapChildren
            >
              <Button
                onClick={handleContinue}
                disabled={isContinueDisabled}
                color="primary"
                size="sm"
              >
                <HStack gap={2}>
                  {(isVerifying || isContinuing) && (
                    <Spinner size="xs" label="" />
                  )}
                  <span>Continue</span>
                </HStack>
              </Button>
            </Tooltip>
          }
        />
      }
    >
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
        data-testid="data-builder-delivery-step"
      >
        <section
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Text as="h3" fontSize="sm" weight="semibold">
            Delivery Method
          </Text>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: '12px',
            }}
          >
            {deliveryOptions.map((option) => (
              <DeliveryMethodOption
                key={option.value}
                value={option.value}
                label={option.label}
                detail={option.detail}
                icon={option.icon}
                isSelected={deliveryMethod === option.value}
                disabled={option.disabled}
                formContent={
                  <Box mt="3">{getOptionFormContent(option.value)}</Box>
                }
                verifyError={
                  deliveryMethod === option.value &&
                  shouldShowSelectedMethodVerifyError ? (
                    <Text
                      data-testid="data-builder-verify-error"
                      style={{
                        color: 'var(--ui-text-error)',
                        marginTop: '0.5rem',
                      }}
                      sizes="sm"
                    >
                      {verifyErrorMessage}
                    </Text>
                  ) : undefined
                }
                onClick={() => selectMethod(option.value)}
              />
            ))}
          </div>
        </section>
      </div>
    </DataBuilderStepLayout>
  );
};
